import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  RouterStateSnapshot,
} from '@angular/router';
import { AlertService } from '../services/alert.service';
import { AuthService } from '../services/auth.service';
import { RoleAuthorisationService } from '../services/role-authorisation.service';
import {
  getRequiredRolesForPortalPath,
  normalizePortalPath,
} from '../config/portal-route-roles';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RoleAuthorisationGuard implements CanActivate, CanActivateChild {
  constructor(
    private _roleAuth: RoleAuthorisationService,
    private _alertService: AlertService,
    private _authService: AuthService
  ) {}

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    return this._checkPortalAuthorization(state);
  }

  async canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    return this._checkPortalAuthorization(state);
  }

  private async _checkPortalAuthorization(state: RouterStateSnapshot): Promise<boolean> {
    // Existing behaviour: let unauthenticated flow through (Amplify handles login inside portal).
    if (!this._authService.isAuthenticated()) {
      return true;
    }

    // Customers use a separate app — block the staff portal entirely (including shell-only /portal).
    const cognitoRoles = this._authService.getRoles();
    if (cognitoRoles.includes('Customer')) {
      this._alertService.errorAlert(
        'This portal is for staff. Please use the customer website to sign in.'
      );
      await this._authService.signOut();
      const url = environment.customerPortalUrl;
      if (url && url.length > 0) {
        window.location.assign(url);
      }
      return false;
    }

    // Do NOT call authenticateUser() here — it uses bypassCache:true and hits Cognito every time.
    // This guard runs on canActivate + canActivateChild (often twice per navigation). AppComponent
    // already refreshes the session once on init; Hub updates roles on sign-in.

    const portalPath = normalizePortalPath(state.url);
    const roles = getRequiredRolesForPortalPath(portalPath);

    // Shell-only /portal: any signed-in user
    if (roles === null) {
      return true;
    }

    const authorized = this._roleAuth.isAuthorised(roles);
    if (!authorized) {
      this._alertService.errorAlert('Unauthorized access');
    }
    return authorized;
  }
}
